import { useQuery } from '@tanstack/react-query';
import { APITypes, MedicalDataTypes } from '../../primary-record-types';
import { getVaccineSearchCacheKey } from './cacheKeys';
import { appAPI } from '../../utils/api';
import urls from '../../utils/urls';
import { getIntl } from '../../utils/intl';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import { useMemo } from 'react';
import _uniqBy from 'lodash/uniqBy';

const intl = getIntl('useVaccineTypeSearch', {
    vaccinationLoadFailure: 'Failed to vaccination types',
});

export type UseVaccineTypeSearchArgs = {
    medicalProfileId: string;
    search: string;
};

export type UseVaccineTypeSearchResult = {
    vaccineTypes: MedicalDataTypes.Coding[];
    isLoading: boolean;
};

export const useVaccineTypeSearch = ({
    medicalProfileId,
    search,
}: UseVaccineTypeSearchArgs): UseVaccineTypeSearchResult => {
    const userQuery = useQuery({
        queryKey: getVaccineSearchCacheKey(search),
        queryFn: async () => {
            const body: APITypes.medicalDataService.SearchVaccinationTypesRequest = {
                searchString: search,
            };
            return await appAPI.post<APITypes.medicalDataService.SearchVaccinationTypesResponse>(
                urls.api.medicalData.elementSearch.vaccineTypes(medicalProfileId),
                {
                    body,
                },
            );
        },
        enabled: !!search,
    });
    useQueryErrorNotify(userQuery, intl('vaccinationLoadFailure'));

    const aggregatedResults = useMemo<MedicalDataTypes.Coding[]>(() => {
        if (!userQuery.data) {
            return [];
        }

        const combined = [
            ...(userQuery.data.exactMatch ? [userQuery.data.exactMatch] : []),
            ...(userQuery.data.substringSearchResults || []),
            ...(userQuery.data.fuzzySearchResults || []),
        ];

        return _uniqBy(combined, (item) => item.code);
    }, [userQuery.data]);

    return {
        vaccineTypes: aggregatedResults,
        isLoading: userQuery.isLoading,
    };
};
